import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface PlanSubmissionSuccessModalProps {
  open: boolean;
  onClose: () => void;
  caseId: number | null;
  clientName: string;
  clientId: string;
}

/**
 * Plan Submission Success Modal
 *
 * Displays confirmation of successful plan submission with:
 * - Success animation/icon
 * - Case ID for reference
 * - Client name
 * - Button to view client details
 * - Auto-close after 10 seconds with countdown
 */
const PlanSubmissionSuccessModal: React.FC<PlanSubmissionSuccessModalProps> = ({
  open,
  onClose,
  caseId,
  clientName,
  clientId
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!open) {
      setCountdown(10);
      setProgress(100);
      return;
    }

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Progress bar (decreases from 100 to 0 over 10 seconds)
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - 10;
        return newProgress < 0 ? 0 : newProgress;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(progressTimer);
    };
  }, [open, onClose]);

  const handleViewClientDetails = () => {
    navigate(`/dashboard/my-clients/${clientId}`);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400 // Higher than default Dialog z-index (1300) to appear above other modals
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ textAlign: 'center', py: 5, px: 4 }}>
          {/* Success Icon */}
          <Box
            sx={{
              mb: 3,
              animation: 'scaleIn 0.5s ease-out',
              '@keyframes scaleIn': {
                '0%': {
                  transform: 'scale(0)',
                  opacity: 0
                },
                '50%': {
                  transform: 'scale(1.1)'
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1
                }
              }
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: '#4caf50'
              }}
            />
          </Box>

          {/* Title */}
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Plan Request Submitted Successfully!
          </Typography>

          {/* Case ID */}
          {caseId && (
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                px: 3,
                py: 1.5,
                my: 2,
                border: '2px solid #e0e0e0'
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Case ID
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                #{caseId}
              </Typography>
            </Box>
          )}

          {/* Client Name */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            For: <strong>{clientName}</strong>
          </Typography>

          {/* Email Notification Message */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            A confirmation email has been sent with tracking details.
          </Typography>

          {/* Action Button */}
          <Button
            variant="contained"
            size="large"
            onClick={handleViewClientDetails}
            sx={{
              mb: 2,
              px: 4,
              py: 1.5,
              bgcolor: '#2e7d32',
              '&:hover': {
                bgcolor: '#1b5e20'
              }
            }}
          >
            View Client Details
          </Button>

          {/* Auto-close countdown */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Closing automatically in {countdown} seconds...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#2e7d32'
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PlanSubmissionSuccessModal;
